import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  opsAdjustVendorPenaltyAmount,
  opsEnableVendorWithoutPayment,
  opsGetVendorDetail,
  opsGetVendorPenalties,
  opsListVendors,
  opsPushVendorPenalty,
  opsSaveVendor,
  opsSaveVendorVehicle,
} from "../../../api/adminOpsApi";
import { autocompleteVehicleLocation } from "../../../api/ownerCarsApi";

function cls(...xs) {
  return xs.filter(Boolean).join(" ");
}

function dt(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

function money(v) {
  const n = Number(v || 0);
  return `Rs ${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function penaltyReasonLabel(code) {
  const safe = String(code || "").trim().toUpperCase();
  if (safe === "VENDOR_CANCEL_WITHIN_24H") return "Late cancellation";
  if (safe === "ADMIN_PUSH_UNFULFILLED_AFTER_ACCEPT") return "Admin pushed unfulfilled";
  return safe || "-";
}

function penaltyStatusTone(status) {
  const safe = String(status || "").trim().toUpperCase();
  if (safe === "PAID") return "good";
  if (safe === "ADMIN_ENABLED") return "ink";
  if (safe === "PENDING_PAYMENT") return "bad";
  return "neutral";
}

function isPendingPenalty(status) {
  return String(status || "").trim().toUpperCase() === "PENDING_PAYMENT";
}

function penaltySummaryLabel(summary) {
  if (summary?.blocked) return "Penalty Blocked";
  if (Number(summary?.outstandingPenaltyCount || 0) > 0) return "Penalty Open";
  return "Penalty Clear";
}

function penaltySummaryTone(summary) {
  if (summary?.blocked) return "bad";
  if (Number(summary?.outstandingPenaltyCount || 0) > 0) return "warn";
  return "neutral";
}

function downloadCsv(filename, rows) {
  const escape = (value) => {
    const text = String(value ?? "");
    if (text.includes(",") || text.includes("\"") || text.includes("\n")) {
      return `"${text.replace(/"/g, "\"\"")}"`;
    }
    return text;
  };
  const csv = rows.map((row) => row.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function Tag({ label, tone = "neutral" }) {
  const tones = {
    neutral: "border-slate-200 bg-slate-50 text-slate-700",
    good: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warn: "border-amber-200 bg-amber-50 text-amber-800",
    bad: "border-rose-200 bg-rose-50 text-rose-700",
    ink: "border-slate-900 bg-slate-900 text-white",
  };
  return <span className={cls("inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-semibold", tones[tone])}>{label}</span>;
}

function StatCard({ label, value, hint = "" }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
      <div className="mt-1 text-sm text-slate-500">{hint}</div>
    </div>
  );
}

function ReadonlyField({ label, value }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="min-h-[44px] rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-900">
        {value || "-"}
      </div>
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder = "" }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none focus:border-slate-400"
      />
    </label>
  );
}

function LocationAutocompleteInput({ label, value, onChange, placeholder = "Search location" }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const q = String(value || "").trim();
    if (q.length < 3) {
      setItems([]);
      setBusy(false);
      return undefined;
    }
    let alive = true;
    setBusy(true);
    autocompleteVehicleLocation(q)
      .then((rows) => {
        if (!alive) return;
        setItems(Array.isArray(rows) ? rows.slice(0, 5) : []);
      })
      .catch(() => {
        if (!alive) return;
        setItems([]);
      })
      .finally(() => {
        if (alive) setBusy(false);
      });
    return () => {
      alive = false;
    };
  }, [value]);

  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="relative">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none focus:border-slate-400"
        />
        {busy ? <div className="mt-2 text-xs text-slate-500">Searching locations...</div> : null}
        {!busy && items.length ? (
          <div className="absolute z-10 mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
            {items.map((item) => (
              <button
                key={`${item.placeId || item.fullText}-${item.latitude || ""}-${item.longitude || ""}`}
                type="button"
                onClick={() => {
                  onChange(item.fullText || "");
                  setItems([]);
                }}
                className="block w-full border-b border-slate-100 px-3 py-3 text-left last:border-b-0 hover:bg-slate-50"
              >
                <div className="text-sm font-semibold text-slate-900">{item.primaryText || item.fullText}</div>
                <div className="mt-1 text-xs text-slate-500">{item.secondaryText || item.fullText}</div>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </label>
  );
}

function ModalShell({ open, title, subtitle, onClose, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/30 p-4">
      <div className="w-full max-w-3xl rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Vendors</div>
            <h3 className="mt-1 text-2xl font-black text-slate-900">{title}</h3>
            <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700">
            Close
          </button>
        </div>
        <div className="mt-5">{children}</div>
        {footer ? <div className="mt-5 flex justify-end gap-2">{footer}</div> : null}
      </div>
    </div>
  );
}

function VendorDetailDrawer({
  open,
  detail,
  detailBusy,
  detailErr,
  onClose,
  onAddCar,
  onPushPenalty,
  onEnableWithoutPayment,
  onAdjustPenaltyAmount,
  actionBusy,
}) {
  const detailVehicles = Array.isArray(detail?.vehicles) ? detail.vehicles : [];
  const recentCases = Array.isArray(detail?.recentCases) ? detail.recentCases : [];
  const penalties = Array.isArray(detail?.penalties) ? detail.penalties : [];
  const penaltySummary = detail?.penaltySummary || null;
  const hasOpenPenalties = Number(penaltySummary?.outstandingPenaltyCount || 0) > 0;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/30">
      <div className="h-full w-full max-w-3xl overflow-y-auto border-l border-slate-200 bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Vendor Details</div>
            <h3 className="mt-1 text-2xl font-black text-slate-900">{detail?.name || "Vendor Drawer"}</h3>
            <p className="mt-1 text-sm text-slate-600">Review vendor profile, linked cars, and recent scheduled activity without leaving the table.</p>
          </div>
          <div className="flex items-center gap-2">
            {detail?.id ? (
              <button type="button" onClick={onAddCar} className="rounded-xl border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-bold text-white">
                Add Car
              </button>
            ) : null}
            <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700">
              Close
            </button>
          </div>
        </div>

        {!detail?.id ? (
          <div className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm font-semibold text-slate-500">
            Select a vendor row to open details here.
          </div>
        ) : detailErr ? (
          <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-5 text-sm font-semibold text-rose-700">{detailErr}</div>
        ) : detailBusy ? (
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white px-4 py-5 text-sm font-semibold text-slate-500">Loading vendor details...</div>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="rounded-3xl border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-lg font-black text-slate-900">Vendor Profile</div>
                  <div className="mt-1 text-sm text-slate-500">Read-only vendor profile for the selected row.</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {detail?.active === false ? <Tag label="Hidden" tone="warn" /> : <Tag label="Visible" tone="good" />}
                  <Tag label={penaltySummaryLabel(penaltySummary)} tone={penaltySummaryTone(penaltySummary)} />
                </div>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <ReadonlyField label="Vendor Name" value={detail?.name} />
                <ReadonlyField label="Phone Number" value={detail?.phone} />
                <div className="md:col-span-2">
                  <ReadonlyField label="Base Location" value={detail?.baseLocation} />
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="text-lg font-black text-slate-900">Penalty And Reactivation</div>
                  <div className="mt-1 text-sm text-slate-500">Track vendor defaults, payment reactivation, and admin-enabled resolutions in one place.</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={onPushPenalty}
                    disabled={actionBusy}
                    className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-bold text-rose-700 disabled:opacity-60"
                  >
                    {actionBusy ? "Working..." : "Apply Charges + Disable"}
                  </button>
                  <button
                    type="button"
                    onClick={onEnableWithoutPayment}
                    disabled={actionBusy || !hasOpenPenalties}
                    className="rounded-xl border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
                  >
                    {actionBusy ? "Working..." : "Resolve Without Payment"}
                  </button>
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <StatCard label="Open Penalties" value={Number(penaltySummary?.outstandingPenaltyCount || 0)} hint="Pending payment rows" />
                <StatCard label="Late Cancellation Charges" value={money(penaltySummary?.outstandingPenaltyAmount || 0)} hint="Current amount blocking bookings" />
                <StatCard label="Latest Trigger" value={penaltyReasonLabel(penaltySummary?.latestReasonCode)} hint={penaltySummary?.message || "No active penalty block."} />
              </div>

              {!penalties.length ? (
                <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm font-semibold text-slate-500">
                  No vendor penalty history yet.
                </div>
              ) : (
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                  <div className="grid grid-cols-12 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    <div className="col-span-3">Reason</div>
                    <div className="col-span-2">Amount</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Disabled</div>
                    <div className="col-span-3">Resolution</div>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {penalties.map((row) => (
                      <div key={row.id} className="grid grid-cols-12 gap-3 px-4 py-4 text-sm">
                        <div className="col-span-3">
                          <div className="font-extrabold text-slate-900">{penaltyReasonLabel(row.reasonCode)}</div>
                          <div className="mt-1 text-xs text-slate-500">{row.planKey || (row.bookingId ? `Booking #${row.bookingId}` : row.tripRequestId ? `Trip #${row.tripRequestId}` : "-")}</div>
                        </div>
                        <div className="col-span-2">
                          <div className="font-bold text-slate-900">{money(row.fineAmount)}</div>
                          {isPendingPenalty(row.status) ? (
                            <button
                              type="button"
                              onClick={() => onAdjustPenaltyAmount(row)}
                              disabled={actionBusy}
                              className="mt-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-bold text-slate-700 disabled:opacity-60"
                            >
                              Adjust Amount
                            </button>
                          ) : null}
                        </div>
                        <div className="col-span-2">
                          <Tag label={String(row.status || "-").replaceAll("_", " ")} tone={penaltyStatusTone(row.status)} />
                        </div>
                        <div className="col-span-2 text-slate-700">
                          <div>{dt(row.disabledAt)}</div>
                          <div className="mt-1 text-xs text-slate-500">{row.pickupAt ? `Pickup ${dt(row.pickupAt)}` : "-"}</div>
                        </div>
                        <div className="col-span-3 text-slate-700">
                          <div className="font-semibold text-slate-900">{row.resolvedBySource ? String(row.resolvedBySource).replaceAll("_", " ") : "-"}</div>
                          <div className="mt-1 text-xs text-slate-500">
                            {row.paymentVerifiedAt ? `Paid ${dt(row.paymentVerifiedAt)}` : row.resolvedAt ? `Resolved ${dt(row.resolvedAt)}` : "Awaiting payment"}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">{row.resolutionNote || row.note || "-"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-lg font-black text-slate-900">Linked Vendor Cars</div>
                  <div className="mt-1 text-sm text-slate-500">Simple car list with consent and visibility state.</div>
                </div>
              </div>
              {!detailVehicles.length ? (
                <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm font-semibold text-slate-500">
                  No cars linked yet for this vendor.
                </div>
              ) : (
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                  <div className="grid grid-cols-12 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    <div className="col-span-4">Vehicle</div>
                    <div className="col-span-4">Base Location</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2 text-right">Consent</div>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {detailVehicles.map((vehicle) => (
                      <div key={vehicle.id} className="grid grid-cols-12 gap-3 px-4 py-4 text-sm">
                        <div className="col-span-4">
                          <div className="font-extrabold text-slate-900">{[vehicle.brand, vehicle.model].filter(Boolean).join(" ") || "-"}</div>
                        </div>
                        <div className="col-span-4 text-slate-700">{vehicle.baseLocation || "-"}</div>
                        <div className="col-span-2">
                          <div className="flex flex-wrap gap-2">
                            <Tag label={vehicle.active === false ? "Hidden" : "Visible"} tone={vehicle.active === false ? "warn" : "good"} />
                            <Tag label={vehicle.consentAccepted === false ? "Consent missing" : "Consent ok"} tone={vehicle.consentAccepted === false ? "bad" : "neutral"} />
                          </div>
                        </div>
                        <div className="col-span-2 flex justify-end">
                          {detail?.id && vehicle?.id && vehicle.consentAccepted !== false ? (
                            <Link
                              to={`/admin/ops/vendor-consents/${detail.id}/${vehicle.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center rounded-xl border border-slate-900 bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-800"
                            >
                              Consent PDF
                            </Link>
                          ) : (
                            <span className="inline-flex items-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-400">
                              Unavailable
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-5">
              <div className="text-lg font-black text-slate-900">Recent Vendor Cases</div>
              <div className="mt-1 text-sm text-slate-500">Scheduled request and booking history for this vendor.</div>
              {!recentCases.length ? (
                <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm font-semibold text-slate-500">
                  No scheduled request activity has reached this vendor yet.
                </div>
              ) : (
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                  <div className="grid grid-cols-12 gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    <div className="col-span-4">Trip</div>
                    <div className="col-span-3">Request</div>
                    <div className="col-span-3">Booking</div>
                    <div className="col-span-2 text-right">Open</div>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {recentCases.map((row) => (
                      <div key={row.id} className="grid grid-cols-12 gap-3 px-4 py-4 text-sm">
                        <div className="col-span-4">
                          <div className="font-extrabold text-slate-900">#{row.tripRequestId || "-"}</div>
                          <div className="mt-1 text-xs text-slate-500">{dt(row.pickupAt)}</div>
                        </div>
                        <div className="col-span-3">
                          <div className="font-semibold text-slate-900">#{row.bookingMatchRequestId || "-"}</div>
                          <div className="mt-1 text-xs text-slate-500">{[row.brand, row.model].filter(Boolean).join(" ") || `Case ${row.caseIndex}`}</div>
                        </div>
                        <div className="col-span-3">
                          <div className="font-extrabold text-slate-900">{row.finalBookingId ? `#${row.finalBookingId}` : "-"}</div>
                          <div className="mt-1 text-xs text-slate-500">{money(row.totalFare)}</div>
                        </div>
                        <div className="col-span-2 flex justify-end">
                          <div className="flex flex-col items-end gap-2">
                            <Link to={`/admin/ops/trip-requests/${row.tripRequestId}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">
                              Request
                            </Link>
                            {row.finalBookingId ? (
                              <Link to={`/admin/ops/bookings/${row.finalBookingId}`} className="rounded-xl border border-slate-900 bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-800">
                                Booking
                              </Link>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const emptyVendorForm = {
  name: "",
  phone: "",
  baseLocation: "",
  active: true,
};

const emptyVehicleForm = {
  brand: "",
  model: "",
  baseLocation: "",
  active: true,
  consentAccepted: true,
};

function VendorCreateModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState(emptyVendorForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) {
      setForm(emptyVendorForm);
      setBusy(false);
      setErr("");
    }
  }, [open]);

  async function submit() {
    setBusy(true);
    setErr("");
    try {
      const saved = await opsSaveVendor(form);
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Failed to save vendor");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Add New Vendor"
      subtitle="Create a vendor with the same simple profile used across operations."
      footer={
        <>
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={submit}
            className="rounded-xl border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
          >
            {busy ? "Saving..." : "Save Vendor"}
          </button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <TextInput label="Vendor Name" value={form.name} onChange={(value) => setForm((s) => ({ ...s, name: value }))} />
        <TextInput label="Phone Number" value={form.phone} onChange={(value) => setForm((s) => ({ ...s, phone: value }))} />
        <div className="md:col-span-2">
          <LocationAutocompleteInput label="Base Location" value={form.baseLocation} onChange={(value) => setForm((s) => ({ ...s, baseLocation: value }))} />
        </div>
      </div>
      {err ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">{err}</div> : null}
    </ModalShell>
  );
}

function VendorVehicleModal({ open, vendor, onClose, onSaved }) {
  const [form, setForm] = useState(emptyVehicleForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) {
      setForm(emptyVehicleForm);
      setBusy(false);
      setErr("");
    }
  }, [open]);

  async function submit() {
    if (!vendor?.id) {
      setErr("Select a vendor first.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await opsSaveVendorVehicle(vendor.id, form);
      onSaved?.(vendor.id);
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Failed to save vendor car");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Add Vendor Car"
      subtitle={vendor?.name ? `Link a new supply car to ${vendor.name}.` : "Link a new supply car to the selected vendor."}
      footer={
        <>
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={submit}
            className="rounded-xl border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-60"
          >
            {busy ? "Saving..." : "Add Car"}
          </button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <TextInput label="Brand" value={form.brand} onChange={(value) => setForm((s) => ({ ...s, brand: value }))} />
        <TextInput label="Model" value={form.model} onChange={(value) => setForm((s) => ({ ...s, model: value }))} />
        <div className="md:col-span-2">
          <LocationAutocompleteInput label="Car Base Location" value={form.baseLocation} onChange={(value) => setForm((s) => ({ ...s, baseLocation: value }))} />
        </div>
      </div>
      {err ? <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">{err}</div> : null}
    </ModalShell>
  );
}

export default function AdminVendorsWorkspace({
  title = "Vendors",
  subtitle = "Full visibility across vendor profile, linked cars, request acceptance, alignment state, and recent scheduled activity.",
  compactHeader = false,
}) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState("");
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [toggleBusyId, setToggleBusyId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const loadList = useCallback(async (nextSelectedId = null) => {
    setBusy(true);
    setErr("");
    try {
      const res = await opsListVendors({ q });
      const nextItems = Array.isArray(res?.items) ? res.items : [];
      setItems(nextItems);
      const fallbackId = nextSelectedId ?? selectedId ?? nextItems[0]?.id ?? null;
      setSelectedId(fallbackId);
    } catch (e) {
      setErr(e?.message || "Failed to load vendors");
      setItems([]);
      setSelectedId(null);
    } finally {
      setBusy(false);
    }
  }, [q, selectedId]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const loadDetail = useCallback(async (vendorId) => {
    if (!vendorId) {
      setDetail(null);
      return;
    }
    setDetailBusy(true);
    setDetailErr("");
    try {
      const [vendorRes, penaltyRes] = await Promise.all([
        opsGetVendorDetail(vendorId),
        opsGetVendorPenalties(vendorId).catch(() => null),
      ]);
      const merged = vendorRes ? { ...vendorRes } : null;
      if (merged && penaltyRes) {
        merged.penaltySummary = penaltyRes;
        merged.penalties = Array.isArray(penaltyRes?.penalties) ? penaltyRes.penalties : (merged.penalties || []);
      }
      setDetail(merged);
    } catch (e) {
      setDetailErr(e?.message || "Failed to load vendor detail");
      setDetail(null);
    } finally {
      setDetailBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let alive = true;
    loadDetail(selectedId).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [loadDetail, selectedId]);

  const summary = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc.vendors += 1;
        acc.vehicles += Number(item?.vehicleCount || 0);
        acc.requests += Number(item?.requestCount || 0);
        acc.bookings += Number(item?.bookingCount || 0);
        if (item?.penaltySummary?.blocked) acc.blocked += 1;
        return acc;
      },
      { vendors: 0, vehicles: 0, requests: 0, bookings: 0, blocked: 0 }
    );
  }, [items]);

  async function handleVendorSaved(saved) {
    const nextId = saved?.id ?? null;
    await loadList(nextId);
    setSelectedId(nextId);
  }

  async function handleVehicleSaved(vendorId) {
    await loadList(vendorId);
    setSelectedId(vendorId);
    await loadDetail(vendorId);
  }

  function exportTable() {
    const rows = [
      ["Vendor", "Phone", "Base Location", "Cars", "Requests", "Bookings", "Visibility", "Penalty Block", "Late Cancellation Charges", "Last Activity"],
      ...items.map((item) => [
        item.name || "",
        item.phone || "",
        item.baseLocation || "",
        item.vehicleCount || 0,
        item.requestCount || 0,
        item.bookingCount || 0,
        item.active === false ? "Hidden" : "Visible",
        item?.penaltySummary?.blocked ? "Blocked" : "Clear",
        item?.penaltySummary?.outstandingPenaltyAmount || 0,
        dt(item.lastCaseAt || item.updatedAt),
      ]),
    ];
    downloadCsv("vendor-directory.csv", rows);
  }

  async function toggleVendorActive(vendor) {
    if (!vendor?.id) return;
    setToggleBusyId(vendor.id);
    setErr("");
    try {
      await opsSaveVendor({
        id: vendor.id,
        name: vendor.name || "",
        phone: vendor.phone || "",
        email: vendor.email || "",
        companyName: vendor.companyName || "",
        baseLocation: vendor.baseLocation || "",
        profileImageUrl: vendor.profileImageUrl || "",
        active: vendor.active === false,
      });
      await loadList(vendor.id);
      setSelectedId(vendor.id);
      await loadDetail(vendor.id);
    } catch (e) {
      setErr(e?.message || "Failed to update vendor visibility");
    } finally {
      setToggleBusyId(null);
    }
  }

  async function handlePushPenalty() {
    if (!detail?.id || actionBusy) return;
    const amountInput = window.prompt("Late Cancellation Charges in INR", String(detail?.penaltySummary?.outstandingPenaltyAmount || 1000));
    if (amountInput == null) return;
    const amount = Number(amountInput);
    if (!Number.isFinite(amount) || amount < 0) {
      window.alert("Enter a valid penalty amount.");
      return;
    }
    const note = window.prompt("Reason or note for this vendor block", "Vendor accepted scheduled work but did not fulfill the ride.") || "";
    const tripRequestIdInput = window.prompt("Optional trip request id", "");
    const bookingIdInput = window.prompt("Optional booking id", "");
    const pickupAtInput = window.prompt("Optional pickup time in YYYY-MM-DD HH:mm format", "");
    setActionBusy(true);
    setErr("");
    try {
      await opsPushVendorPenalty(detail.id, {
        tripRequestId: tripRequestIdInput ? Number(tripRequestIdInput) : null,
        bookingId: bookingIdInput ? Number(bookingIdInput) : null,
        pickupAt: pickupAtInput || "",
        amount,
        note,
      });
      await loadList(detail.id);
      await loadDetail(detail.id);
    } catch (e) {
      setErr(e?.message || "Failed to push vendor penalty");
    } finally {
      setActionBusy(false);
    }
  }

  async function handleEnableWithoutPayment() {
    if (!detail?.id || actionBusy || Number(detail?.penaltySummary?.outstandingPenaltyCount || 0) <= 0) return;
    const note = window.prompt("Optional note for penalty resolution", "Penalty resolved by admin without payment.") || "";
    setActionBusy(true);
    setErr("");
    try {
      await opsEnableVendorWithoutPayment(detail.id, { note });
      await loadList(detail.id);
      await loadDetail(detail.id);
    } catch (e) {
      setErr(e?.message || "Failed to enable vendor");
    } finally {
      setActionBusy(false);
    }
  }

  async function handleAdjustPenaltyAmount(row) {
    if (!detail?.id || !row?.id || actionBusy || !isPendingPenalty(row.status)) return;
    const amountInput = window.prompt("Adjust late cancellation charges in INR", String(row.fineAmount || detail?.penaltySummary?.outstandingPenaltyAmount || 0));
    if (amountInput == null) return;
    const amount = Number(amountInput);
    if (!Number.isFinite(amount) || amount <= 0) {
      window.alert("Enter a valid penalty amount greater than zero.");
      return;
    }
    const note = window.prompt("Optional note for fine amount update", "Adjusted by admin after review.") || "";
    setActionBusy(true);
    setErr("");
    try {
      await opsAdjustVendorPenaltyAmount(detail.id, row.id, { amount, note });
      await loadList(detail.id);
      await loadDetail(detail.id);
    } catch (e) {
      setErr(e?.message || "Failed to adjust vendor penalty amount");
    } finally {
      setActionBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <VendorCreateModal open={vendorModalOpen} onClose={() => setVendorModalOpen(false)} onSaved={handleVendorSaved} />
      <VendorVehicleModal open={vehicleModalOpen} vendor={detail} onClose={() => setVehicleModalOpen(false)} onSaved={handleVehicleSaved} />
      <VendorDetailDrawer
        open={drawerOpen}
        detail={detail}
        detailBusy={detailBusy}
        detailErr={detailErr}
        onClose={() => setDrawerOpen(false)}
        onAddCar={() => setVehicleModalOpen(true)}
        onPushPenalty={() => void handlePushPenalty()}
        onEnableWithoutPayment={() => void handleEnableWithoutPayment()}
        onAdjustPenaltyAmount={(row) => void handleAdjustPenaltyAmount(row)}
        actionBusy={actionBusy}
      />

      <div className="rounded-3xl border border-slate-200 bg-white p-5">
        <div className={cls("flex gap-4", compactHeader ? "items-end justify-between" : "flex-col lg:flex-row lg:items-start lg:justify-between")}>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Vendors</div>
            <h1 className="mt-1 text-2xl font-black text-slate-900">{title}</h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-600">{subtitle}</p>
            <p className="mt-2 max-w-3xl text-xs font-semibold uppercase tracking-wider text-slate-500">
              Simple directory-style visibility only. Vendor details stay read-only here, and new vendor or new car creation happens through modal forms.
            </p>
          </div>
          <div className="flex w-full max-w-xl gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search vendor, phone, brand, model, reg no"
              className="h-11 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none focus:border-slate-400"
            />
            <button type="button" onClick={() => setVendorModalOpen(true)} className="rounded-xl border border-slate-900 bg-slate-900 px-4 text-sm font-bold text-white">
              Add New Vendor
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Vendors" value={summary.vendors} hint="Directory rows available" />
          <StatCard label="Vendor Cars" value={summary.vehicles} hint="Linked supply cars" />
          <StatCard label="Scheduled Requests" value={summary.requests} hint="Vendor-side scheduled demand" />
          <StatCard label="Created Bookings" value={summary.bookings} hint="Bookings created from vendor supply" />
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-1 xl:grid-cols-1">
          <StatCard label="Penalty Blocked Vendors" value={summary.blocked} hint="Currently blocked until payment or admin enable" />
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
	        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
	          <div>
	            <div className="text-lg font-black text-slate-900">Vendor Directory Table</div>
	            <div className="mt-1 text-sm text-slate-500">Same clean tabular view style as your directory pages, with no inline editing.</div>
	          </div>
	          <div className="flex items-center gap-2">
	            <button
	              type="button"
	              onClick={exportTable}
	              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
	            >
	              Export Data
	            </button>
	            {selectedId ? (
	              <button
	                type="button"
	                onClick={() => setVehicleModalOpen(true)}
	                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
	              >
	                Add Car to Selected Vendor
	              </button>
	            ) : null}
	          </div>
	        </div>

        {busy ? (
          <div className="px-5 py-8 text-sm font-semibold text-slate-500">Loading vendors...</div>
        ) : err ? (
          <div className="px-5 py-8 text-sm font-semibold text-rose-700">{err}</div>
        ) : !items.length ? (
          <div className="px-5 py-8 text-sm font-semibold text-slate-500">No vendors found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3">Vendor</th>
                  <th className="px-5 py-3">Phone</th>
                  <th className="px-5 py-3">Company</th>
                  <th className="px-5 py-3">Base Location</th>
                  <th className="px-5 py-3">Cars</th>
                  <th className="px-5 py-3">Requests</th>
                  <th className="px-5 py-3">Bookings</th>
                  <th className="px-5 py-3">Visibility</th>
                  <th className="px-5 py-3">Penalty</th>
                  <th className="px-5 py-3">Last Activity</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {items.map((item) => {
                  const active = item.active !== false;
                  const selected = String(selectedId) === String(item.id);
                  return (
                    <tr key={item.id} className={cls("hover:bg-slate-50", selected && "bg-slate-50/70")}>
                      <td className="px-5 py-4">
                        <div className="font-bold text-slate-900">{item.name || "-"}</div>
                        <div className="mt-1 text-xs text-slate-500">{item.email || "-"}</div>
                      </td>
                      <td className="px-5 py-4 font-semibold text-slate-700">{item.phone || "-"}</td>
                      <td className="px-5 py-4 text-slate-700">{item.companyName || "-"}</td>
                      <td className="px-5 py-4 text-slate-700">{item.baseLocation || "-"}</td>
                      <td className="px-5 py-4 font-bold text-slate-900">{item.vehicleCount || 0}</td>
                      <td className="px-5 py-4 font-bold text-slate-900">{item.requestCount || 0}</td>
                      <td className="px-5 py-4 font-bold text-slate-900">{item.bookingCount || 0}</td>
                      <td className="px-5 py-4">
                        <Tag label={active ? "Visible" : "Hidden"} tone={active ? "good" : "warn"} />
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-2">
                          <Tag
                            label={item?.penaltySummary?.blocked ? "Blocked" : "Clear"}
                            tone={item?.penaltySummary?.blocked ? "bad" : "neutral"}
                          />
                          {item?.penaltySummary?.blocked ? (
                            <span className="text-xs font-semibold text-rose-700">{money(item?.penaltySummary?.outstandingPenaltyAmount || 0)}</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-slate-700">{dt(item.lastCaseAt || item.updatedAt)}</td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
	                          <button
	                            type="button"
	                            onClick={() => {
	                              setSelectedId(item.id);
	                              setDrawerOpen(true);
	                            }}
	                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
	                          >
	                            View
                          </button>
	                          <button
	                            type="button"
	                            onClick={() => {
	                              setSelectedId(item.id);
	                              setVehicleModalOpen(true);
	                            }}
	                            className="rounded-xl border border-slate-900 bg-slate-900 px-3 py-2 text-xs font-bold text-white"
	                          >
	                            Add Car
	                          </button>
	                          <button
	                            type="button"
	                            disabled={toggleBusyId === item.id}
	                            onClick={() => toggleVendorActive(item)}
	                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
	                          >
	                            {toggleBusyId === item.id ? "Saving..." : active ? "Disable" : "Enable"}
	                          </button>
	                        </div>
	                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
